import React from "react";
import { formatPhoneNumber } from "react-phone-number-input";
import { ICustomer } from "../../../@types";
import { createCustomer } from "../../../backend-services";
import { CustomerT } from "../../../types";
import { validatePhoneNumber } from "../../../utils";
import { CreateModal } from "../../common/CreateModal";
import { CreateCustomer } from "../../customers";
import CreateForm from "../../customers/Create/CreateForm";
import { showToast } from "../../../utils/toastUtils";

interface CreateProps {
  showSideModal: boolean;
  setShowSideModal: (show: boolean) => void;
  handleSetCustomer: (selectedCustomer: ICustomer | null) => void;
  onCustomerCreate?: (customer: any) => void;
  createCustomers?: (newCustomer: any) => void;
  fetchCustomers?: () => void;
  onClose?: () => void;
}

const CreateCustomerModal: React.FC<CreateProps> = ({
  handleSetCustomer,
  showSideModal,
  setShowSideModal,
  onCustomerCreate,
  createCustomers,
  fetchCustomers,
  onClose,
}) => {
  const [isOpen, setIsOpen] = React.useState(false);
  const [isLoading, setIsLoading] = React.useState(false);
  const [errorMessage, setErrorMessage] = React.useState("");
  const [backendErrorMessage, setBackendErrorMessage] = React.useState("");
  const [formData, setFormData] = React.useState<ICustomer>({} as ICustomer);

  function isErrorResponse(
    error: any,
  ): error is { response: { data: { message: string } } } {
    return (
      typeof error === "object" &&
      error.response &&
      typeof error.response === "object" &&
      error.response.data &&
      typeof error.response.data === "object" &&
      error.response.data.message
    );
  }

  const handleCreateCustomer = async (customer: CustomerT) => {
    setIsLoading(true);
    setErrorMessage("");
    setBackendErrorMessage("");

    // Validate phone number
    if (!customer.phone || !validatePhoneNumber(customer.phone)) {
      setErrorMessage("Please input a valid phone number");
      setBackendErrorMessage("");
      setIsLoading(false);
      return;
    }

    try {
      let data;
      if (onCustomerCreate) {
        data = await createCustomer({
          ...customer,
          phone: customer.phone,
          isOptedIn: false,
          outletId: "",
        });
      } else if (createCustomers) {
        data = createCustomers({
          ...customer,
          phone: customer.phone,
          outletId: "",
        });
      }

      setIsLoading(false);

      // Check for existing customer error explicitly from backend
      if (data && data.message && data.message.includes("already exists")) {
        showToast( "A customer with this phone number or email already exists.", "error")
      } else {
        fetchCustomers && fetchCustomers();
        setIsOpen(false);
        showToast("Customer created successfully!", "success")

        if (onCustomerCreate) {
          onCustomerCreate({
            id: data.id,
            name: `${data.firstName} ${data.lastName}`,
            email: data.email,
            businessName: data.businessName,
            phone: data.phone,
            address: data.address,
            city: data.city,
            state: data.state,
          });
        }
      }
    } catch (error: any) {
      setIsLoading(false);

      if (isErrorResponse(error)) {
        const errorMessage = error.response.data.message;
        setErrorMessage(errorMessage);
        setBackendErrorMessage(errorMessage);

        // Display toast error if email or phone already exists
        if (errorMessage.includes("already exists")) {
          showToast("A customer with this phone number or email already exists.", "error")
        } else {
          showToast(errorMessage, "error")
        }
      } else {
        setErrorMessage(error.message);
        setBackendErrorMessage(error.message);
        showToast(error.message, "error")
      }
    }
  };

  const handleClose = () => {
    setIsOpen(false);
    onClose && onClose();
  };

  return (
    <>
      <div className="hidden md:block">
        <div className="fixed top-0 left-0 w-screen h-screen bg-gray-800 bg-opacity-30 flex items-center justify-center z-10 ">
          <div className="bg-white p-4 rounded-lg shadow-lg">
            <CreateForm
              isLoading={isLoading}
              show={true}
              onSubmit={handleCreateCustomer}
              buttontitle={"Add"}
              backendErrorMessage={backendErrorMessage}
              setFormData={setFormData}
              onClose={handleClose}
              title={"Add Customer"}
            />
          </div>
        </div>
      </div>
      <div className="md:hidden">
        <CreateCustomer
          show={showSideModal}
          onClose={() => setShowSideModal(false)}
          onCustomerCreate={(customer) => {
            handleSetCustomer(customer);
            setShowSideModal(false);
          }}
        />
      </div>
    </>
  );
};

export { CreateCustomerModal };
